import type { KeyboardEventHandler, ReactNode } from 'react';
import { Input } from '@/components/cms/ui/input';
import { Card, CardHeading } from '@/components/ui/card';
import LureoButton from '@/common/LureoButton';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { FieldLabel } from '@/components/ui/text';

export interface PasswordValues {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface SecurityCardProps {
  values: PasswordValues;
  onChange: (field: keyof PasswordValues, value: string) => void;
  /** Enter in each field: on to the next, and from the last updates the password. */
  onCurrentPasswordKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onNewPasswordKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onConfirmPasswordKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  saving: boolean;
  onUpdatePassword: () => void;
  /** Under Update Password: the way to set a password for an account without one. */
  footer?: ReactNode;
}

/** My Account's Security tab: changing the password. */
export function SecurityCard({ values, onChange, onCurrentPasswordKeyDown, onNewPasswordKeyDown, onConfirmPasswordKeyDown, saving, onUpdatePassword, footer }: SecurityCardProps) {
  return (
    <Card elevation="high">
      <CardHeading title="Security Settings" description="Manage your password and security preferences" className="mb-6" />
      <div className="space-y-6">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <FieldLabel variant="plain" htmlFor="password_current_password" className="leading-none">Current Password</FieldLabel>
            <Input
              id="password_current_password"
              type="password"
              autoComplete="new-password"
              value={values.current_password}
              onChange={(e) => onChange('current_password', e.target.value)}
              onKeyDown={onCurrentPasswordKeyDown}
              placeholder="Enter your current password"
            />
          </div>
          <div className="grid gap-2">
            <FieldLabel variant="plain" htmlFor="new_password" className="leading-none">New Password</FieldLabel>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              value={values.new_password}
              onChange={(e) => onChange('new_password', e.target.value)}
              onKeyDown={onNewPasswordKeyDown}
              placeholder="Enter new password"
            />
          </div>
          <div className="grid gap-2">
            <FieldLabel variant="plain" htmlFor="confirm_password" className="leading-none">Confirm New Password</FieldLabel>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              value={values.confirm_password}
              onChange={(e) => onChange('confirm_password', e.target.value)}
              onKeyDown={onConfirmPasswordKeyDown}
              placeholder="Confirm new password"
            />
          </div>
        </div>

        <LureoButton
          onClick={onUpdatePassword}
          variant="outline-3"
          className="h-9 px-4 rounded-lureo-radius-button-pill"
          disabled={saving || !values.current_password || !values.new_password}>
          {saving && <LoadingSpinner size="sm" className="mr-2" />}
          Update Password
        </LureoButton>

        {footer}
      </div>
    </Card>
  );
}
